import "./TrackerMixpanel.d";

import type { ConfigResponse } from "@zoom/appssdk";
import log from "loglevel";
import mixpanel from "mixpanel-browser/dist/mixpanel-core.cjs";

import Config from "@readai/common/config";
import type { Client, MeetingPlatform } from "@readai/common/types";
import { snakeKeys } from "@readai/common/utils/formatUtils";

import type { TrackerProvider, TrackerUser } from "./types";
import { getEventFromPageTitle } from "./utils";

type PageView = {
  page_title?: string;
  page_location?: string;
  page_path?: string;
};

export interface InitProps {
  propertyId: string;
  debug?: boolean;
  apiHost?: string;
  cookieDomain?: string;
}

export class TrackerMixpanel implements TrackerProvider {
  protected mixpanel: typeof mixpanel;
  private propertyId: string;
  private debug: boolean;
  private apiHost?: string;
  private cookieDomain?: string;

  private lastIdentifiedUserId: string | null;

  constructor() {
    this.mixpanel = mixpanel;
    this.propertyId = "";
    this.debug = false;
    this.lastIdentifiedUserId = null;
  }

  initialize({ propertyId, debug, apiHost, cookieDomain }: InitProps) {
    this.propertyId = propertyId;
    this.debug = debug ?? false;
    this.apiHost = apiHost;
    this.cookieDomain = cookieDomain;

    this.mixpanel.init(this.propertyId, {
      debug: this.debug,
      api_host: this.apiHost ?? Config.tracking.MIXPANEL_HOST,
      cookie_domain: this.cookieDomain ?? window.location.host,
    });
  }

  resetUser() {
    // No-op we no longer want to reset a user's mixpanel id
    // because mixpanel has a 500 distinct id limit and we only
    // have a handful of logged out events
    // see: https://docs.mixpanel.com/docs/tracking-methods/id-management/migrating-to-simplified-id-merge-system#on-original-id-merge
    //
    // this.lastIdentifiedUserId = null;
    // this.mixpanel.reset();
  }

  setUser(
    user: TrackerUser | null,
    currentUser?: TrackerUser | null,
    client?: Client | null,
  ) {
    if (!user) {
      this.lastIdentifiedUserId = null;
      return;
    }

    if (this.lastIdentifiedUserId !== user.id) {
      this.mixpanel.identify(user.id);
      this.lastIdentifiedUserId = user.id;
    }

    if (user && currentUser) {
      return;
    }

    this.mixpanel.people.set({
      distinct_id: user.id,
      $first_name: user.firstName,
      $last_name: user.lastName,
      $email: user.email,
      calendar_permission_level: user.calendarPermissionLevel,
      preferred_language: user.preferredLanguage,
    });

    if (client) {
      this.mixpanel.people.set_once({ source: client });
    }
  }

  setMeetingPlatform(meetingPlatform: MeetingPlatform) {
    this.mixpanel.register({ meeting_platform: meetingPlatform });
  }

  setClient(client: Client) {
    this.mixpanel.register({ client_app: client });
  }

  event(action: string, params?: object) {
    if (!Config.tracking.ENABLED) {
      log.info("tracking [disabled] event:", action, snakeKeys(params));
      return;
    }

    this.mixpanel.track(action, snakeKeys(params));
  }

  pageviewEvent(pageview: PageView) {
    const title = pageview.page_title;
    const path = pageview.page_path || window.location.pathname;

    if (!Config.tracking.ENABLED) {
      log.info("tracking [disabled] pageviewEvent:", title, location, path);
      return;
    }

    // eslint-disable-next-line lingui/no-unlocalized-strings
    const eventName = (title && getEventFromPageTitle(title)) ?? "Page View";
    this.mixpanel.track(eventName, { page_title: title, page_path: path });
  }

  setRunningContext(runningContext: string) {
    this.mixpanel.register({ running_context: runningContext });
  }

  setMeetingRole(meetingRole: string) {
    // The mixpanel type library is way out of date and there isn't a newer one
    // to use, so until we have that, we'll add this ts-ignore
    this.mixpanel.register(
      { meeting_role: meetingRole },
      { persistent: false },
    );
  }

  setZoomConfig(zoomConfig: ConfigResponse) {
    this.mixpanel.register({ zoom_client_version: zoomConfig.clientVersion });
    this.mixpanel.people.set({
      zoom_client_version: zoomConfig.clientVersion,
      zoom_browser_version: zoomConfig.browserVersion,
    });
  }

  setPeopleProperties(properties: object) {
    this.mixpanel.people.set(properties);
  }

  register(params: object) {
    this.mixpanel.register(params);
  }

  startSessionRecording() {
    // No-op
  }

  stopSessionRecording() {
    // No-op
  }
}

const tracker = new TrackerMixpanel();

export default tracker;
